import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Scale, ArrowLeft, Users, Activity, AlertCircle, CheckCircle,
  FileText, MessageSquare, Clock, TrendingUp, Shield, Settings,
  ThumbsUp, ThumbsDown, AlertTriangle, Lock, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AuthContext } from "@/App";

// Use relative URL in production (deployed), or env variable in development
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminConsole = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [metrics, setMetrics] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [lockedAnswers, setLockedAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const [metricsRes, logsRes, usersRes, escRes, lockedRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/metrics?days=30`),
        axios.get(`${BACKEND_URL}/api/admin/audit-logs?limit=50`),
        axios.get(`${BACKEND_URL}/api/admin/users`),
        axios.get(`${BACKEND_URL}/api/hitl/escalations`),
        axios.get(`${BACKEND_URL}/api/hitl/locked-answers`)
      ]);
      
      setMetrics(metricsRes.data);
      setAuditLogs(logsRes.data.logs || []);
      setUsers(usersRes.data.users || []);
      setEscalations(escRes.data.escalations || []);
      setLockedAnswers(lockedRes.data.answers || []);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      if (error.response?.status === 403) {
        toast.error("Access denied. Admin permissions required.");
        navigate("/dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRoleUpdate = async (userId, newRole) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users/role`, {
        target_user_id: userId,
        new_role: newRole
      });
      toast.success("Role updated successfully");
      fetchAdminData();
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const handleApprove = async (lockId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/hitl/approve`, {
        lock_id: lockId
      });
      toast.success("Answer approved");
      fetchAdminData();
    } catch (error) {
      toast.error("Failed to approve");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C10]" data-testid="admin-console">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/dashboard")}
              className="text-[#94A3B8] hover:text-[#E2E8F0]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-[#C5A059]" />
              <span className="font-playfair text-lg font-semibold text-[#E2E8F0]">Admin Console</span>
            </div>
          </div>
        </div>
      </header>

      <div className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#15171C] border border-white/10 mb-6">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#C5A059]/10 data-[state=active]:text-[#C5A059]">
              <Activity className="w-4 h-4 mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#C5A059]/10 data-[state=active]:text-[#C5A059]">
              <Users className="w-4 h-4 mr-2" /> Users
            </TabsTrigger>
            <TabsTrigger value="reviews" className="data-[state=active]:bg-[#C5A059]/10 data-[state=active]:text-[#C5A059]">
              <CheckCircle className="w-4 h-4 mr-2" /> Reviews
            </TabsTrigger>
            <TabsTrigger value="logs" className="data-[state=active]:bg-[#C5A059]/10 data-[state=active]:text-[#C5A059]">
              <FileText className="w-4 h-4 mr-2" /> Audit Logs
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <MetricCard
                icon={<MessageSquare className="w-5 h-5" />}
                label="Total Prompts"
                value={metrics?.total_prompts || 0}
                sublabel="Last 30 days"
              />
              <MetricCard
                icon={<Clock className="w-5 h-5" />}
                label="Avg Latency"
                value={`${metrics?.avg_latency_ms || 0}ms`}
                sublabel="Response time"
              />
              <MetricCard
                icon={<Users className="w-5 h-5" />}
                label="Active Users"
                value={metrics?.unique_users || 0}
                sublabel="Unique users"
              />
              <MetricCard
                icon={<TrendingUp className="w-5 h-5" />}
                label="Satisfaction"
                value={`${metrics?.feedback?.satisfaction_rate || 0}%`}
                sublabel="Positive feedback"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Feedback Breakdown */}
              <Card className="bg-[#15171C] border-white/5 p-6">
                <h3 className="font-playfair text-lg text-[#E2E8F0] mb-4">User Feedback</h3>
                <div className="space-y-3">
                  <FeedbackBar
                    icon={<ThumbsUp className="w-4 h-4 text-[#10B981]" />}
                    label="Helpful"
                    count={metrics?.feedback?.breakdown?.helpful || 0}
                    total={metrics?.feedback?.total_feedback || 1}
                    color="bg-[#10B981]"
                  />
                  <FeedbackBar
                    icon={<ThumbsUp className="w-4 h-4 text-[#4A6FA5]" />}
                    label="Thumbs Up"
                    count={metrics?.feedback?.breakdown?.thumbs_up || 0}
                    total={metrics?.feedback?.total_feedback || 1}
                    color="bg-[#4A6FA5]"
                  />
                  <FeedbackBar
                    icon={<ThumbsDown className="w-4 h-4 text-[#EF4444]" />}
                    label="Thumbs Down"
                    count={metrics?.feedback?.breakdown?.thumbs_down || 0}
                    total={metrics?.feedback?.total_feedback || 1}
                    color="bg-[#EF4444]"
                  />
                  <FeedbackBar
                    icon={<AlertCircle className="w-4 h-4 text-[#F59E0B]" />}
                    label="Needs Review"
                    count={metrics?.feedback?.breakdown?.needs_review || 0}
                    total={metrics?.feedback?.total_feedback || 1}
                    color="bg-[#F59E0B]"
                  />
                </div>
              </Card>

              {/* Pending Items */}
              <Card className="bg-[#15171C] border-white/5 p-6">
                <h3 className="font-playfair text-lg text-[#E2E8F0] mb-4">Pending Actions</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-[#0B0C10] rounded-sm">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-[#F59E0B]" />
                      <span className="text-[#E2E8F0]">Escalations</span>
                    </div>
                    <Badge className="bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30">
                      {metrics?.pending_escalations || 0}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-[#0B0C10] rounded-sm">
                    <div className="flex items-center gap-3">
                      <Lock className="w-5 h-5 text-[#4A6FA5]" />
                      <span className="text-[#E2E8F0]">Pending Approvals</span>
                    </div>
                    <Badge className="bg-[#4A6FA5]/10 text-[#4A6FA5] border-[#4A6FA5]/30">
                      {metrics?.pending_approvals || 0}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-[#0B0C10] rounded-sm">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-[#EF4444]" />
                      <span className="text-[#E2E8F0]">Errors (30d)</span>
                    </div>
                    <Badge className="bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/30">
                      {metrics?.total_errors || 0}
                    </Badge>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-[#15171C] border-white/5">
              <ScrollArea className="h-[600px]">
                <table className="w-full">
                  <thead className="bg-[#0B0C10] sticky top-0">
                    <tr>
                      <th className="text-left p-4 text-[#64748B] font-mono text-xs uppercase">User</th>
                      <th className="text-left p-4 text-[#64748B] font-mono text-xs uppercase">Email</th>
                      <th className="text-left p-4 text-[#64748B] font-mono text-xs uppercase">Role</th>
                      <th className="text-left p-4 text-[#64748B] font-mono text-xs uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.user_id} className="border-t border-white/5">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#C5A059] flex items-center justify-center text-[#0B0C10] font-medium">
                              {u.name?.[0] || "U"}
                            </div>
                            <span className="text-[#E2E8F0] font-manrope">{u.name}</span>
                          </div>
                        </td>
                        <td className="p-4 text-[#94A3B8] font-mono text-sm">{u.email}</td>
                        <td className="p-4">
                          <Select
                            value={u.role || "solicitor"}
                            onValueChange={(value) => handleRoleUpdate(u.user_id, value)}
                          >
                            <SelectTrigger className="w-32 bg-[#0B0C10] border-white/10 text-[#E2E8F0]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#15171C] border-white/10">
                              <SelectItem value="admin" className="text-[#E2E8F0]">Admin</SelectItem>
                              <SelectItem value="partner" className="text-[#E2E8F0]">Partner</SelectItem>
                              <SelectItem value="associate" className="text-[#E2E8F0]">Associate</SelectItem>
                              <SelectItem value="solicitor" className="text-[#E2E8F0]">Solicitor</SelectItem>
                              <SelectItem value="psl" className="text-[#E2E8F0]">PSL</SelectItem>
                              <SelectItem value="paralegal" className="text-[#E2E8F0]">Paralegal</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-4">
                          <Button variant="ghost" size="sm" className="text-[#94A3B8] hover:text-[#E2E8F0]">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </Card>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Escalations */}
              <Card className="bg-[#15171C] border-white/5 p-6">
                <h3 className="font-playfair text-lg text-[#E2E8F0] mb-4">Escalations</h3>
                <ScrollArea className="h-[400px]">
                  {escalations.length === 0 ? (
                    <p className="text-[#64748B] text-center py-8">No pending escalations</p>
                  ) : (
                    <div className="space-y-3">
                      {escalations.map((esc) => (
                        <div key={esc.escalation_id} className="p-4 bg-[#0B0C10] rounded-sm border border-white/5">
                          <div className="flex items-start justify-between mb-2">
                            <Badge className="bg-[#F59E0B]/10 text-[#F59E0B]">Pending</Badge>
                            <span className="text-[#64748B] font-mono text-xs">
                              {new Date(esc.escalated_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-[#E2E8F0] text-sm mb-2">{esc.reason}</p>
                          <Button size="sm" className="bg-[#C5A059] text-[#0B0C10] hover:bg-[#D4AF37]">
                            Review
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </Card>

              {/* Locked Answers */}
              <Card className="bg-[#15171C] border-white/5 p-6">
                <h3 className="font-playfair text-lg text-[#E2E8F0] mb-4">Locked Answers</h3>
                <ScrollArea className="h-[400px]">
                  {lockedAnswers.length === 0 ? (
                    <p className="text-[#64748B] text-center py-8">No locked answers pending</p>
                  ) : (
                    <div className="space-y-3">
                      {lockedAnswers.map((answer) => (
                        <div key={answer.lock_id} className="p-4 bg-[#0B0C10] rounded-sm border border-white/5">
                          <div className="flex items-start justify-between mb-2">
                            <Badge className={answer.status === "approved" ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#4A6FA5]/10 text-[#4A6FA5]"}>
                              {answer.status}
                            </Badge>
                            <span className="text-[#64748B] font-mono text-xs">
                              {new Date(answer.locked_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-[#E2E8F0] text-sm mb-2 line-clamp-2">{answer.content?.substring(0, 150)}...</p>
                          {answer.status === "locked" && (
                            <Button 
                              size="sm" 
                              onClick={() => handleApprove(answer.lock_id)}
                              className="bg-[#10B981] text-white hover:bg-[#059669]"
                            >
                              Approve
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </Card>
            </div>
          </TabsContent>

          {/* Audit Logs Tab */}
          <TabsContent value="logs">
            <Card className="bg-[#15171C] border-white/5">
              <ScrollArea className="h-[600px]">
                <table className="w-full">
                  <thead className="bg-[#0B0C10] sticky top-0">
                    <tr>
                      <th className="text-left p-4 text-[#64748B] font-mono text-xs uppercase">Time</th>
                      <th className="text-left p-4 text-[#64748B] font-mono text-xs uppercase">Type</th>
                      <th className="text-left p-4 text-[#64748B] font-mono text-xs uppercase">User</th>
                      <th className="text-left p-4 text-[#64748B] font-mono text-xs uppercase">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((log) => (
                      <tr key={log.log_id} className="border-t border-white/5">
                        <td className="p-4 text-[#94A3B8] font-mono text-xs">
                          {new Date(log.timestamp).toLocaleString()}
                        </td>
                        <td className="p-4">
                          <Badge className={getEventTypeBadgeClass(log.event_type)}>
                            {log.event_type}
                          </Badge>
                        </td>
                        <td className="p-4 text-[#E2E8F0] font-mono text-sm">{log.user_id?.substring(0, 12)}...</td>
                        <td className="p-4 text-[#94A3B8] text-sm">
                          {log.action || log.endpoint || log.prompt?.substring(0, 50)}...
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const MetricCard = ({ icon, label, value, sublabel }) => (
  <Card className="bg-[#15171C] border-white/5 p-6">
    <div className="flex items-center gap-3 mb-3">
      <div className="text-[#C5A059]">{icon}</div>
      <span className="text-[#94A3B8] font-mono text-xs uppercase">{label}</span>
    </div>
    <p className="text-3xl font-playfair text-[#E2E8F0] mb-1">{value}</p>
    <p className="text-[#64748B] font-mono text-xs">{sublabel}</p>
  </Card>
);

const FeedbackBar = ({ icon, label, count, total, color }) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-[#94A3B8] text-sm">{label}</span>
        </div>
        <span className="text-[#E2E8F0] font-mono text-sm">{count}</span>
      </div>
      <div className="h-2 bg-[#0B0C10] rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
};

const getEventTypeBadgeClass = (type) => {
  switch (type) {
    case "prompt": return "bg-[#4A6FA5]/10 text-[#4A6FA5]";
    case "action": return "bg-[#C5A059]/10 text-[#C5A059]";
    case "auth": return "bg-[#10B981]/10 text-[#10B981]";
    case "error": return "bg-[#EF4444]/10 text-[#EF4444]";
    default: return "bg-[#64748B]/10 text-[#64748B]";
  }
};

export default AdminConsole;
